// src/components/prescriptions/QrOverlay.jsx
import React from "react";
import { useNavigate } from "react-router-dom";
import TopBarCenter from "@/sot/primitives/TopBarCenter";
import Button from "@/components/ui/Button.jsx";
import SignatureStampLine from "@/components/prescriptions/SignatureStampLine.jsx";
import { useTheme } from "@/contexts/ThemeContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { savePrescriptionVerifyContext } from "@/utils/prescriptionVerification.js";
import {
  createDoctorStampOverlayMarkup,
  formatDoctorStampName,
} from "@/components/prescriptions/doctorStampPresentation.js";

/**
 * ✅ Single source of truth for the stamp.
 * - Base elephant PNG (center mark)
 * - Double rings
 * - Curved top + bottom text with UNIQUE ids per instance (no collisions)
 */
export function DoctorStamp({
  baseSrc,
  doctorName,
  regNo,
  sizePx = 70,
  className = "",
  style,
  isDark = false,
}) {
  const uidRef = React.useRef(`stamp-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`);
  const baseFilter = isDark
    ? "grayscale(1) brightness(1.18) contrast(0.92) opacity(0.82)"
    : "none";

  return (
    <div className={["relative", className].join(" ")} style={{ width: sizePx, height: sizePx, ...style }}>
      <img
        src={baseSrc}
        alt="Doctor stamp"
        className="absolute inset-0 h-full w-full object-contain"
        draggable="false"
        style={{ filter: baseFilter }}
      />

      <svg
        className="absolute inset-0"
        viewBox="0 0 100 100"
        xmlns="http://www.w3.org/2000/svg"
        dangerouslySetInnerHTML={{
          __html: createDoctorStampOverlayMarkup({
            doctorName: formatDoctorStampName(doctorName),
            regNo,
            idPrefix: uidRef.current,
            isDark,
          }),
        }}
      />
    </div>
  );
}

/**
 * ✅ Shared QR overlay (Doctor + Patient)
 * - Mobile: AfriMed Sheet primitive (so paddings are consistent)
 * - Desktop: centered dialog
 * - Shows QR + elephant badge + prescriber + signature + stamp
 */
export default function QrOverlay({
  open,
  onClose,
  rx,
  qrSrc,

  // assets
  qrElephantSrc,
  sigPlaceholderSrc,
  stampBaseSrc,

  // overrides (optional)
  signatureSrc,
  doctorName,
  regNo,
  verifyPath = "",
  verifyUrl = "",
  verifyState = null,
}) {
  const nav = useNavigate();
  const { isDark } = useTheme();
  const { t } = useLanguage();
  const [copyState, setCopyState] = React.useState("idle");

  // ESC to close (desktop + keyboards)
  React.useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose, open]);

  React.useEffect(() => {
    if (open) setCopyState("idle");
  }, [open, verifyUrl]);

  if (!open) return null;

  const medName =
    rx?.medicineName || rx?.medicationName || t("prescriptions.common.medication", "Medication");
  const form = rx?.form || rx?.strengthLabel || "";
  const ingredients = rx?.ingredients || rx?.composition || "";

  const dName =
    doctorName ||
    rx?.doctor?.prescriptionName ||
    rx?.doctor?.name ||
    t("prescriptions.common.prescriber", "Prescriber");
  const dReg = regNo || rx?.doctor?.regNo || "";
  const sigSrc = signatureSrc || rx?.signature_image || rx?.doctor?.signatureSrc || sigPlaceholderSrc;
  const canOpenVerify = !!String(verifyPath || "").trim();
  const canCopyVerifyUrl = !!String(verifyUrl || "").trim();

  async function handleCopyVerifyLink() {
    if (!canCopyVerifyUrl) return;

    try {
      if (!navigator?.clipboard?.writeText) throw new Error("Clipboard unavailable");
      await navigator.clipboard.writeText(String(verifyUrl).trim());
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    }
  }

  function handleOpenVerify() {
    if (!canOpenVerify) return;

    if (verifyState) savePrescriptionVerifyContext(verifyState);
    onClose?.();
    nav(String(verifyPath).trim(), {
      state: verifyState || null,
    });
  }

  const content = (
    <div className="w-full" style={{ fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial" }}>
      {/* Header */}
      <div className="text-title-large text-on-surface">{medName}</div>
      {form ? <div className="mt-1 text-body-medium text-on-surface">{form}</div> : null}
      {ingredients ? <div className="mt-1 text-body-medium text-on-surface-var">{ingredients}</div> : null}

      {/* QR block */}
      <div className="mt-4 rounded-[12px] border border-[rgba(var(--clr-outline-variant),0.55)] bg-[rgb(var(--clr-surface-container))] p-2.5 md:mt-5 md:p-3">
        <div className="relative mx-auto aspect-square w-full max-w-[340px] md:max-w-[420px]">
          <div className="h-full w-full rounded-[10px] bg-white p-2 md:p-3">
            <img
              src={qrSrc}
              alt={t("prescriptions.common.qrCode", "QR code")}
              className="h-full w-full object-contain"
              draggable="false"
            />
          </div>

          {/* On mobile, always overlay a neutral AfriMed elephant so branded QR centers stay consistent. */}
          {qrElephantSrc ? (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white md:hidden">
                <img src={qrElephantSrc} alt="" className="h-9 w-9 grayscale" draggable="false" />
              </div>
            </div>
          ) : null}
        </div>
      </div>

    {/* Prescriber + dotted line */}
      <div className="mt-3 text-body-small text-on-surface-var md:mt-4">
        {t("prescriptions.common.prescriber", "Prescriber")}: <span className="text-on-surface">{dName}</span>
      </div>

    <SignatureStampLine
        sigSrc={sigSrc}
        stampBaseSrc={stampBaseSrc}
        doctorName={dName}
        regNo={dReg}
        stampIsDark={isDark}
        stampSizePx={88}
        sigClassName="h-[60px] w-[108px] md:h-[68px] md:w-[120px]"
        sigNudgeY={2}
        stampNudgeY={2}
        />

      <div className="mt-2 text-body-small text-on-surface-var md:mt-3">
        {t(
          "prescriptions.wizard.poweredBy",
          "Powered by AfriMed™. The prescriber remains responsible for clinical decisions.",
        )}
      </div>

      {canOpenVerify || canCopyVerifyUrl ? (
        <div className="mt-3 md:mt-4">
          <div className="text-body-small text-on-surface-var">
            {t(
              "prescriptions.qrOverlay.fallbackHelp",
              "Need a fallback? Open the verify route here or copy the link for manual checking.",
            )}
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            {canOpenVerify ? (
              <Button variant="tonal" size="sm" onClick={handleOpenVerify}>
                {t("prescriptions.qrOverlay.openVerifyPage", "Open verify page")}
              </Button>
            ) : null}

            {canCopyVerifyUrl ? (
              <Button variant="ghost" size="sm" onClick={handleCopyVerifyLink}>
                {copyState === "copied"
                  ? t("prescriptions.qrOverlay.linkCopied", "Link copied")
                  : copyState === "failed"
                    ? t("prescriptions.qrOverlay.copyFailed", "Copy failed")
                    : t("prescriptions.qrOverlay.copyLink", "Copy link")}
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );

  // Desktop dialog (centered)
  const Desktop = (
    <div className="hidden md:flex fixed inset-0 z-[120] items-center justify-center px-6" role="presentation" onMouseDown={onClose}>
      <div
        className="w-full max-w-[560px] rounded-[24px] bg-[rgb(var(--clr-surface-cont-highest))] shadow-[0_30px_90px_rgba(2,6,23,0.25)] p-6"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <TopBarCenter title="" onBack={onClose} close margin="mb-2" />
        {content}
      </div>
    </div>
  );

  const Mobile = (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center px-4 md:hidden"
      style={{
        paddingTop: "max(env(safe-area-inset-top), 16px)",
        paddingBottom: "max(env(safe-area-inset-bottom), 16px)",
      }}
      role="presentation"
      onMouseDown={onClose}
    >
      <div
        className="w-full max-w-[420px] overflow-y-auto rounded-[24px] bg-[rgb(var(--clr-surface-cont-highest))] px-4 pb-5 pt-3 shadow-[0_30px_90px_rgba(2,6,23,0.25)]"
        style={{ maxHeight: "calc(100dvh - max(env(safe-area-inset-top), 16px) - max(env(safe-area-inset-bottom), 16px) - 16px)" }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <TopBarCenter title="" onBack={onClose} close margin="mb-2" />
        <div className="pb-1">{content}</div>
      </div>
    </div>
  );

  return (
    <>
      <div className="fixed inset-0 z-[110] bg-[rgba(2,6,23,0.55)]" onMouseDown={onClose} aria-hidden="true" />
      {Desktop}
      {Mobile}
    </>
  );
}
